import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  Search,
  X,
  Check,
  Loader2,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Gauge,
  FolderOpen,
  Save,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  ImagePlus,
  Image,
  Edit3,
  Eye,
  ZoomIn,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/admin';
import { Button, Card, Select, Input, Spinner } from '@/components/common';
import { projects as projectsApi, zaehler as zaehlerApi } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Tab component
const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 ${
      active
        ? 'border-primary-500 text-primary-600 bg-primary-50'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
  </button>
);

const KIZaehler = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('erfassen');

  // Version marker for debugging cache issues
  useEffect(() => {
    console.log('🔧 KIZaehler.jsx geladen - Version: 2026-02-07-v1');
  }, []);

  // Determine if mobile layout should be forced (for Bauleiter)
  const isMobileLayout = user?.role === 'bauleiter' || window.innerWidth < 768;

  return (
    <div className={isMobileLayout ? 'mobile-layout' : ''}>
      <PageHeader
        title="Zähler-Erfassung"
        description="Elektrozähler fotografieren und automatisch auslesen"
        icon={Gauge}
      />

      {/* Foto-Erkennung seit 21.10.2026 auf Eis (keine Claude-API, noch kein lokales Bildmodell) */}
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Die automatische Zähler-Erkennung aus Fotos ist vorübergehend deaktiviert. Bereits erfasste Zähler lassen sich weiter prüfen und bearbeiten.
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-lg">
        <TabButton
          active={activeTab === 'erfassen'}
          icon={Camera}
          label="Erfassen"
          onClick={() => setActiveTab('erfassen')}
        />
        <TabButton
          active={activeTab === 'pruefen'}
          icon={Search}
          label="Prüfen"
          onClick={() => setActiveTab('pruefen')}
        />
      </div>

      {/* Tab Content */}
      {activeTab === 'erfassen' ? (
        <ErfassenTab isMobile={isMobileLayout} />
      ) : (
        <PruefenTab isMobile={isMobileLayout} />
      )}
    </div>
  );
};

// ============================================
// ERFASSEN TAB
// ============================================
const ErfassenTab = ({ isMobile }) => {

  // Step state: 1 = Projekt wählen, 2 = Fotos hochladen, 3 = Verarbeitung, 4 = Ergebnis
  const [step, setStep] = useState(1);

  // Form data
  const [selectedProject, setSelectedProject] = useState(null);
  const [bereich, setBereich] = useState('');

  // Photos
  const [photos, setPhotos] = useState([]);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Processing
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [results, setResults] = useState([]);

  // WICHTIG: Base64-Daten separat speichern (nicht im React State!)
  // React State kann bei großen Strings Probleme machen
  const photoBase64MapRef = useRef(new Map());

  // Saving
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Google Drive status
  const [uploadingToDrive, setUploadingToDrive] = useState(false);

  const { data: projects = [], isLoading: loadingProjects } = useQuery({
    queryKey: ['projects-active'],
    queryFn: () => projectsApi.getAll({ status: 'active' }).then(r => r.data || []),
  });

  const { data: driveStatus } = useQuery({
    queryKey: ['zaehler-drive-status'],
    queryFn: () => zaehlerApi.getDriveStatus().then(r => r.data).catch((err) => {
      console.warn('Drive status check failed:', err);
      return { configured: false };
    }),
  });

  const driveConfigured = driveStatus?.configured ?? false;

  // Handle file selection - no limit
  const handleFileSelect = async (e, inputRef) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Warning for large batches
    if (photos.length + files.length > 50) {
      toast.error('Hinweis: Viele Fotos - Verarbeitung kann länger dauern');
    }

    const timestamp = Date.now();
    const newPhotos = [];

    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const photoData = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        driveFileId: null,
        uploading: driveConfigured,
      };
      newPhotos.push(photoData);
    }

    // Add photos immediately (shows upload status)
    setPhotos((prev) => [...prev, ...newPhotos]);

    // Upload to Drive in background if configured
    if (driveConfigured) {
      setUploadingToDrive(true);
      const startIndex = photos.length;

      for (let i = 0; i < newPhotos.length; i++) {
        try {
          const base64 = await fileToBase64(newPhotos[i].file);
          const response = await zaehlerApi.uploadTemp(base64, startIndex + i, timestamp);

          if (response.data.success && response.data.driveConfigured) {
            // Update photo with Drive file ID
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === newPhotos[i].id
                  ? { ...p, driveFileId: response.data.fileId, uploading: false }
                  : p
              )
            );
          } else {
            // Mark as not uploading (failed or not configured)
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === newPhotos[i].id ? { ...p, uploading: false } : p
              )
            );
          }
        } catch (err) {
          console.warn('Drive upload failed for photo:', err);
          setPhotos((prev) =>
            prev.map((p) =>
              p.id === newPhotos[i].id ? { ...p, uploading: false } : p
            )
          );
        }
      }
      setUploadingToDrive(false);
    }

    // Reset both inputs
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  // Remove photo (and delete from Drive if uploaded)
  const removePhoto = async (photoId) => {
    const photo = photos.find((p) => p.id === photoId);

    // Delete from Drive if it was uploaded
    if (photo?.driveFileId) {
      try {
        await zaehlerApi.deleteTemp(photo.driveFileId);
      } catch (err) {
        console.warn('Failed to delete temp file from Drive:', err);
      }
    }

    setPhotos(photos.filter((p) => p.id !== photoId));
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Process photos via Backend API
  const processPhotos = async () => {
    setStep(3);
    setResults([]);
    setCurrentPhotoIndex(0);

    // Clear the base64 map for new processing
    photoBase64MapRef.current.clear();

    const newResults = [];

    for (let i = 0; i < photos.length; i++) {
      setCurrentPhotoIndex(i);
      const photo = photos[i];

      try {
        const base64 = await fileToBase64(photo.file);

        // WICHTIG: Base64 im Ref speichern, NICHT im State!
        photoBase64MapRef.current.set(photo.id, base64);
        console.log(`📸 [${i + 1}/${photos.length}] Base64 in Map gespeichert, photoId: ${photo.id}, length: ${base64.length}`);

        const response = await zaehlerApi.analyze(base64);

        newResults.push({
          success: response.data.success,
          seriennummer: response.data.seriennummer,
          kennnummer: response.data.kennnummer,
          wohnungsnummer: response.data.wohnungsnummer,
          zaehlerstand: response.data.zaehlerstand,
          einheit: response.data.einheit,
          confidence: response.data.confidence,
          photoId: photo.id,
          preview: photo.preview,
          driveFileId: photo.driveFileId,
          foto_base64: base64,  // Store directly in result - survives component remount
        });
      } catch (err) {
        console.error(`❌ [${i + 1}/${photos.length}] Error:`, err.message);
        newResults.push({
          success: false,
          error: err.response?.data?.error || err.message || 'Verbindungsfehler',
          photoId: photo.id,
          preview: photo.preview,
          driveFileId: photo.driveFileId,
          foto_base64: null,
        });
      }

      setResults([...newResults]);
    }

    console.log('✅ Processing complete. Base64 Map size:', photoBase64MapRef.current.size);
    setStep(4);
  };

  // Save results via Backend API
  const saveResults = async () => {
    setSaving(true);

    const successfulResults = results.filter((r) => r.success);
    console.log('📤 Saving', successfulResults.length, 'zaehler. Base64 Map size:', photoBase64MapRef.current.size);

    try {
      // WICHTIG: Base64 aus dem Ref holen, NICHT aus dem State!
      const payload = {
        zaehler: successfulResults.map((r) => {
          // Base64 aus der Map holen via photoId
          const foto_base64 = r.foto_base64 || photoBase64MapRef.current.get(r.photoId) || null;
          console.log(`📸 photoId: ${r.photoId}, foto_base64: ${foto_base64 ? foto_base64.length + ' chars' : 'MISSING!'}`);

          return {
            seriennummer: r.seriennummer || null,
            kennnummer: r.kennnummer || null,
            wohnungsnummer: r.wohnungsnummer || null,
            zaehlerstand: r.zaehlerstand || null,
            einheit: r.einheit || 'kWh',
            foto_base64: foto_base64,
            driveFileId: r.driveFileId || null,
          };
        }),
        project_id: selectedProject.id,
        project_name: selectedProject.name,
        bereich: bereich,
      };

      console.log('📤 Payload size:', JSON.stringify(payload).length, 'bytes');

      const response = await zaehlerApi.save(payload);

      // Show success message with Drive info if available
      const driveCount = response.data.driveResults?.length || 0;
      if (driveCount > 0) {
        toast.success(`${successfulResults.length} Zähler gespeichert! ${driveCount} Fotos in Google Drive verschoben.`);
      } else {
        toast.success(`${successfulResults.length} Zähler erfolgreich gespeichert!`);
      }
      setSaved(true);
    } catch (err) {
      toast.error('Fehler beim Speichern: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setStep(1);
    setSelectedProject(null);
    setBereich('');
    setPhotos([]);
    setResults([]);
    setSaved(false);
    // Base64 Map leeren
    photoBase64MapRef.current.clear();
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary-500" />
              Zähler erfassen
            </h2>

            {loadingProjects ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bauvorhaben *
                  </label>
                  <Select
                    value={selectedProject?.id || ''}
                    onChange={(e) => {
                      const project = projects.find((p) => p.id === e.target.value);
                      setSelectedProject(project || null);
                    }}
                    options={projects.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Bauvorhaben wählen..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bereich *
                  </label>
                  <Input
                    value={bereich}
                    onChange={(e) => setBereich(e.target.value)}
                    placeholder="z.B. Haus 29, Etage 0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Wird für die Ordnerstruktur verwendet
                  </p>
                </div>

                <Button
                  variant="primary"
                  className="w-full mt-4"
                  onClick={() => setStep(2)}
                  disabled={!selectedProject || !bereich.trim()}
                  icon={ChevronRight}
                  iconPosition="right"
                >
                  Weiter
                </Button>
              </div>
            )}
          </Card>
        );

      case 2:
        return (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary-500" />
                Fotos hochladen
              </h2>
              <span className="text-sm text-gray-500">
                {selectedProject?.name} - {bereich}
              </span>
            </div>

            {/* Hidden file inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={(e) => handleFileSelect(e, cameraInputRef)}
              className="hidden"
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e, galleryInputRef)}
              className="hidden"
            />

            {/* Desktop: Drop Zone */}
            {!isMobile && (
              <div
                className="border-2 border-dashed rounded-xl p-8 text-center transition-colors border-primary-300 bg-primary-50 hover:border-primary-400 cursor-pointer mb-4"
                onClick={() => galleryInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const files = Array.from(e.dataTransfer.files).filter((f) =>
                    f.type.startsWith('image/')
                  );
                  handleFileSelect({ target: { files } }, null);
                }}
              >
                <ImagePlus className="w-12 h-12 text-primary-400 mx-auto mb-3" />
                <p className="text-primary-700 font-medium mb-1">
                  Fotos hierher ziehen
                </p>
                <p className="text-sm text-primary-600">
                  oder <span className="underline">Dateien wählen</span>
                </p>
              </div>
            )}

            {/* Mobile: Two buttons for Camera and Gallery */}
            {isMobile && (
              <div className="flex flex-col gap-3 mb-4">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex items-center justify-center gap-3 p-4 bg-primary-500 text-white rounded-xl font-medium text-lg hover:bg-primary-600 transition-colors"
                >
                  <Camera className="w-6 h-6" />
                  Kamera öffnen
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="flex items-center justify-center gap-3 p-4 bg-gray-100 text-gray-700 rounded-xl font-medium text-lg border-2 border-gray-200 hover:bg-gray-200 transition-colors"
                >
                  <Image className="w-6 h-6" />
                  Aus Galerie wählen
                </button>
              </div>
            )}

            {/* Photo Previews */}
            {photos.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">
                    {photos.length} Foto{photos.length !== 1 ? 's' : ''} hochgeladen
                    {uploadingToDrive && (
                      <span className="text-primary-500 ml-2">
                        <Loader2 className="w-4 h-4 inline animate-spin" /> Hochladen...
                      </span>
                    )}
                  </p>
                  {photos.length > 0 && (
                    <button
                      onClick={() => setPhotos([])}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      Alle entfernen
                    </button>
                  )}
                </div>
                <div className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
                  {photos.map((photo, index) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.preview}
                        alt={`Foto ${index + 1}`}
                        className={`w-full h-24 object-cover rounded-lg ${photo.uploading ? 'opacity-60' : ''}`}
                      />
                      {/* Upload spinner overlay */}
                      {photo.uploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        </div>
                      )}
                      {/* Drive uploaded indicator */}
                      {photo.driveFileId && !photo.uploading && (
                        <div className="absolute top-1 left-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                      <button
                        onClick={() => removePhoto(photo.id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 md:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded">
                        {index + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className={`flex gap-3 mt-6 ${isMobile ? 'flex-col' : ''}`}>
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                icon={ChevronLeft}
              >
                Zurück
              </Button>
              <Button
                variant="primary"
                onClick={processPhotos}
                disabled={photos.length === 0}
                icon={Search}
                className={isMobile ? 'w-full' : 'flex-1'}
              >
                Verarbeiten ({photos.length})
              </Button>
            </div>
          </Card>
        );

      case 3:
        return (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
              Verarbeite Fotos...
            </h2>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Fortschritt</span>
                <span>{currentPhotoIndex + 1} / {photos.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all"
                  style={{ width: `${((currentPhotoIndex + 1) / photos.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Status List */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {photos.map((photo, index) => {
                const result = results[index];
                const status =
                  index < currentPhotoIndex ? 'done' :
                  index === currentPhotoIndex ? 'processing' :
                  'pending';

                return (
                  <div
                    key={photo.id}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      status === 'done'
                        ? result?.success
                          ? 'bg-green-50'
                          : 'bg-red-50'
                        : status === 'processing'
                        ? 'bg-primary-50'
                        : 'bg-gray-50'
                    }`}
                  >
                    {status === 'done' ? (
                      result?.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      )
                    ) : status === 'processing' ? (
                      <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                    )}

                    <span className="flex-1">
                      Foto {index + 1}
                      {result?.success && (
                        <span className="text-sm text-gray-500 ml-2">
                          | {result.kennnummer} | {result.wohnungsnummer}
                        </span>
                      )}
                      {result && !result.success && (
                        <span className="text-sm text-red-500 ml-2">
                          {result.error}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        );

      case 4:
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.filter((r) => !r.success).length;

        if (saved) {
          return (
            <Card className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Erfolgreich gespeichert!
              </h2>
              <p className="text-gray-600 mb-6">
                {successCount} Zähler wurden erfasst und gespeichert.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={resetForm}
                  icon={Camera}
                >
                  Weitere erfassen
                </Button>
                <Button
                  variant="outline"
                  onClick={resetForm}
                  icon={Search}
                >
                  Prüfen
                </Button>
              </div>
            </Card>
          );
        }

        return (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Verarbeitung abgeschlossen
            </h2>

            <div className="mb-4 flex gap-4">
              <div className="flex items-center gap-2 text-green-600">
                <Check className="w-5 h-5" />
                <span>{successCount} erkannt</span>
              </div>
              {failCount > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{failCount} nicht erkannt</span>
                </div>
              )}
            </div>

            {/* Results Table */}
            <div className="border rounded-lg overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Kenn.</th>
                    <th className="px-4 py-2 text-left">Whg.</th>
                    <th className="px-4 py-2 text-left">Zähler-Nr.</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {results.map((result, index) => (
                    <tr
                      key={index}
                      className={result.success ? '' : 'bg-red-50'}
                    >
                      <td className="px-4 py-2">
                        {result.success ? result.kennnummer : (
                          <span className="text-red-500 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Fehler
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {result.success ? result.wohnungsnummer : result.error}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {result.success ? result.seriennummer : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className={`flex gap-3 ${isMobile ? 'flex-col' : ''}`}>
              <Button
                variant="outline"
                onClick={resetForm}
                icon={RotateCcw}
              >
                Verwerfen
              </Button>
              <Button
                variant="primary"
                onClick={saveResults}
                loading={saving}
                disabled={successCount === 0}
                icon={Save}
                className={isMobile ? 'w-full' : 'flex-1'}
              >
                Speichern ({successCount})
              </Button>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className={isMobile ? 'pb-20' : ''}>
      {renderStep()}
    </div>
  );
};

// ============================================
// PRÜFEN TAB
// ============================================
const PruefenTab = ({ isMobile }) => {
  const queryClient = useQueryClient();
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [expandedBereich, setExpandedBereich] = useState(null);

  // Modal state for editing
  const [selectedZaehler, setSelectedZaehler] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Delete confirmation state
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, data: null });
  const [deleting, setDeleting] = useState(false);

  // Handle ESC key for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        setLightboxOpen(false);
      }
    };
    if (lightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [lightboxOpen]);

  const { data: projects = [] } = useQuery({
    queryKey: ['projects-active'],
    queryFn: () => projectsApi.getAll({ status: 'active' }).then(r => r.data || []),
  });

  const { data: zaehlerResult, isLoading: loading } = useQuery({
    queryKey: ['zaehler', selectedProjectId],
    queryFn: () => zaehlerApi.getAll(selectedProjectId ? { project_id: selectedProjectId } : {}).then(r => r.data),
  });

  const zaehlerData = zaehlerResult?.grouped ?? {};

  const invalidateZaehler = () => queryClient.invalidateQueries({ queryKey: ['zaehler', selectedProjectId] });

  // Open edit modal
  const openEditModal = (zaehler) => {
    setSelectedZaehler(zaehler);
    setEditForm({
      seriennummer: zaehler.seriennummer || '',
      kennnummer: zaehler.kennnummer || '',
      wohnungsnummer: zaehler.wohnungsnummer || '',
      zaehlerstand: zaehler.zaehlerstand || '',
      einheit: zaehler.einheit || 'kWh',
    });
  };

  // Close edit modal
  const closeEditModal = () => {
    setSelectedZaehler(null);
    setEditForm({});
    setLightboxOpen(false);
  };

  // Save edited zaehler
  const saveZaehler = async () => {
    if (!selectedZaehler) return;
    setSaving(true);
    try {
      await zaehlerApi.update(selectedZaehler.id, editForm);
      toast.success('Zähler erfolgreich aktualisiert');
      closeEditModal();
      invalidateZaehler();
    } catch (err) {
      toast.error('Fehler beim Speichern: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteSingle = async () => {
    if (!deleteModal.data?.id) return;
    setDeleting(true);
    try {
      await zaehlerApi.delete(deleteModal.data.id);
      toast.success('Zähler erfolgreich gelöscht');
      setDeleteModal({ open: false, type: null, data: null });
      closeEditModal();
      invalidateZaehler();
    } catch (err) {
      toast.error('Fehler beim Löschen: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteBereich = async () => {
    if (!deleteModal.data?.projectId || !deleteModal.data?.bereich) return;
    setDeleting(true);
    try {
      const response = await zaehlerApi.deleteBereich(deleteModal.data.projectId, deleteModal.data.bereich);
      toast.success(`${response.data.count} Zähler aus "${deleteModal.data.bereich}" gelöscht`);
      setDeleteModal({ open: false, type: null, data: null });
      invalidateZaehler();
    } catch (err) {
      toast.error('Fehler beim Löschen: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteModal.data?.projectId) return;
    setDeleting(true);
    try {
      const response = await zaehlerApi.deleteProject(deleteModal.data.projectId);
      toast.success(`${response.data.count} Zähler aus Projekt gelöscht`);
      setDeleteModal({ open: false, type: null, data: null });
      invalidateZaehler();
    } catch (err) {
      toast.error('Fehler beim Löschen: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = () => {
    switch (deleteModal.type) {
      case 'single':
        handleDeleteSingle();
        break;
      case 'bereich':
        handleDeleteBereich();
        break;
      case 'project':
        handleDeleteProject();
        break;
      default:
        break;
    }
  };

  const renderBereichDetail = (projectName, bereich, zaehlerList, projectId) => {
    const sorted = [...zaehlerList].sort((a, b) => {
      const aNum = parseInt(a.kennnummer) || 0;
      const bNum = parseInt(b.kennnummer) || 0;
      return aNum - bNum;
    });

    return (
      <div className="mt-4 border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Kenn.</th>
              <th className="px-4 py-2 text-left">Whg.</th>
              <th className="px-4 py-2 text-left">Zähler</th>
              <th className="px-4 py-2 text-left w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sorted.map((zaehler) => (
              <tr
                key={zaehler.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => openEditModal(zaehler)}
              >
                <td className="px-4 py-2 font-medium">{zaehler.kennnummer}</td>
                <td className="px-4 py-2">{zaehler.wohnungsnummer}</td>
                <td className="px-4 py-2 font-mono text-xs">{zaehler.seriennummer}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <button
                      className="p-1 text-gray-400 hover:text-primary-500 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(zaehler);
                      }}
                      title="Anzeigen/Bearbeiten"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModal({
                          open: true,
                          type: 'single',
                          data: zaehler,
                        });
                      }}
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-primary-500" />
          Zähler prüfen
        </h2>

        {/* Filter */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bauvorhaben
          </label>
          <Select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            options={[
              { value: '', label: 'Alle Bauvorhaben' },
              ...projects.map((p) => ({ value: p.id, label: p.name })),
            ]}
            placeholder=""
          />
        </div>

        {/* Data */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : Object.keys(zaehlerData).length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Gauge className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Noch keine Zähler erfasst</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(zaehlerData).map(([projectName, bereiche]) => {
              // Get project ID from first zaehler entry
              const firstBereich = Object.values(bereiche)[0];
              const projectId = firstBereich?.[0]?.project_id;
              const totalZaehler = Object.values(bereiche).reduce((sum, list) => sum + list.length, 0);

              return (
                <div key={projectName} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-3 font-medium flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-primary-500" />
                      {projectName}
                      <span className="text-sm text-gray-500 font-normal">
                        ({totalZaehler} Zähler)
                      </span>
                    </div>
                    <button
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModal({
                          open: true,
                          type: 'project',
                          data: { projectId, projectName, count: totalZaehler },
                        });
                      }}
                      title={`Alle ${totalZaehler} Zähler dieses Projekts löschen`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="divide-y">
                    {Object.entries(bereiche).map(([bereich, zaehlerList]) => {
                      const isExpanded = expandedBereich === `${projectName}-${bereich}`;

                      return (
                        <div key={bereich}>
                          <div className="flex items-center">
                            <button
                              className="flex-1 px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                              onClick={() =>
                                setExpandedBereich(
                                  isExpanded ? null : `${projectName}-${bereich}`
                                )
                              }
                            >
                              <span className="flex items-center gap-2">
                                <span className="text-gray-500">└</span>
                                {bereich}
                                <span className="text-sm text-gray-500">
                                  ({zaehlerList.length})
                                </span>
                              </span>
                              <span className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                                <ChevronRight
                                  className={`w-5 h-5 text-gray-400 transition-transform ${
                                    isExpanded ? 'rotate-90' : ''
                                  }`}
                                />
                              </span>
                            </button>
                            <button
                              className="p-2 mr-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModal({
                                  open: true,
                                  type: 'bereich',
                                  data: { projectId, bereich, count: zaehlerList.length },
                                });
                              }}
                              title={`Alle ${zaehlerList.length} Zähler in "${bereich}" löschen`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="px-4 pb-4">
                              {renderBereichDetail(projectName, bereich, zaehlerList, projectId)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      {selectedZaehler && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Gauge className="w-5 h-5 text-primary-500" />
                Zähler bearbeiten
              </h3>
              <button
                onClick={closeEditModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Photo with Zoom on Hover */}
              {selectedZaehler.foto_path ? (
                <div
                  className="border rounded-lg overflow-hidden bg-gray-100 relative cursor-zoom-in group"
                  onClick={() => setLightboxOpen(true)}
                >
                  <div className="overflow-hidden">
                    <img
                      src={selectedZaehler.foto_path}
                      alt="Zähler-Foto"
                      className="w-full h-auto max-h-[400px] object-contain transition-transform duration-300 ease-out group-hover:scale-[2]"
                      onMouseMove={(e) => {
                        const rect = e.target.getBoundingClientRect();
                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                        const y = ((e.clientY - rect.top) / rect.height) * 100;
                        e.target.style.transformOrigin = `${x}% ${y}%`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transformOrigin = 'center center';
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.nextSibling.style.display = 'flex';
                      }}
                    />
                  </div>
                  <div className="hidden items-center justify-center py-12 text-gray-400">
                    <div className="text-center">
                      <Image className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Foto nicht verfügbar</p>
                    </div>
                  </div>
                  {/* Zoom hint overlay */}
                  <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <ZoomIn className="w-3 h-3" />
                    Klicken für Vollbild
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg bg-gray-50 p-8 text-center text-gray-400">
                  <Image className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Kein Foto vorhanden</p>
                </div>
              )}

              {/* Edit Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zählernummer
                  </label>
                  <Input
                    value={editForm.seriennummer}
                    onChange={(e) =>
                      setEditForm({ ...editForm, seriennummer: e.target.value })
                    }
                    placeholder="1ISK0096707183"
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kennnummer
                  </label>
                  <Input
                    value={editForm.kennnummer}
                    onChange={(e) =>
                      setEditForm({ ...editForm, kennnummer: e.target.value })
                    }
                    placeholder="1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wohnungsnummer
                  </label>
                  <Input
                    value={editForm.wohnungsnummer}
                    onChange={(e) =>
                      setEditForm({ ...editForm, wohnungsnummer: e.target.value })
                    }
                    placeholder="29 oder EG links"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zählerstand
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={editForm.zaehlerstand}
                      onChange={(e) =>
                        setEditForm({ ...editForm, zaehlerstand: e.target.value })
                      }
                      placeholder="000583"
                      className="flex-1"
                    />
                    <Input
                      value={editForm.einheit}
                      onChange={(e) =>
                        setEditForm({ ...editForm, einheit: e.target.value })
                      }
                      className="w-20"
                      placeholder="kWh"
                    />
                  </div>
                </div>
              </div>

              {/* Metadata (read-only) */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500">Projekt:</span>{' '}
                    {selectedZaehler.project_name || '-'}
                  </div>
                  <div>
                    <span className="text-gray-500">Bereich:</span>{' '}
                    {selectedZaehler.bereich || '-'}
                  </div>
                  <div>
                    <span className="text-gray-500">Erstellt:</span>{' '}
                    {selectedZaehler.created_at
                      ? new Date(selectedZaehler.created_at).toLocaleDateString('de-DE')
                      : '-'}
                  </div>
                  <div>
                    <span className="text-gray-500">ID:</span>{' '}
                    {selectedZaehler.id}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between p-4 border-t bg-gray-50">
              <Button
                variant="ghost"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => setDeleteModal({
                  open: true,
                  type: 'single',
                  data: selectedZaehler,
                })}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={closeEditModal}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  icon={saving ? Loader2 : Save}
                  onClick={saveZaehler}
                  disabled={saving}
                >
                  {saving ? 'Speichern...' : 'Speichern'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold">
                  {deleteModal.type === 'single' && 'Zähler löschen'}
                  {deleteModal.type === 'bereich' && 'Etage löschen'}
                  {deleteModal.type === 'project' && 'Alle Zähler des Projekts löschen'}
                </h3>
              </div>

              <p className="text-gray-600 mb-6">
                {deleteModal.type === 'single' && (
                  <>
                    Möchten Sie den Zähler <strong>{deleteModal.data?.kennnummer || deleteModal.data?.seriennummer}</strong> wirklich löschen?
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </>
                )}
                {deleteModal.type === 'bereich' && (
                  <>
                    Möchten Sie alle <strong>{deleteModal.data?.count} Zähler</strong> in <strong>"{deleteModal.data?.bereich}"</strong> wirklich löschen?
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </>
                )}
                {deleteModal.type === 'project' && (
                  <>
                    Möchten Sie alle <strong>{deleteModal.data?.count} Zähler</strong> des Projekts <strong>"{deleteModal.data?.projectName}"</strong> wirklich löschen?
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </>
                )}
              </p>

              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setDeleteModal({ open: false, type: null, data: null })}
                  disabled={deleting}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  className="bg-red-500 hover:bg-red-600"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Löschen...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {deleteModal.type === 'single' ? 'Löschen' : `${deleteModal.data?.count} Zähler löschen`}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox - Full Screen Image */}
      {lightboxOpen && selectedZaehler?.foto_path && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] cursor-zoom-out"
          onClick={() => setLightboxOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setLightboxOpen(false)}
          tabIndex={0}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={selectedZaehler.foto_path}
            alt="Zähler-Foto Vollbild"
            className="max-w-[95vw] max-h-[95vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-lg">
            ESC oder Klick zum Schließen
          </div>
        </div>
      )}
    </div>
  );
};

export default KIZaehler;
